'use client';

import {
	BulkAccountLoader,
	HighLeverageModeConfigAccountSubscriber,
	PollingHighLeverageModeConfigAccountSubscriber,
	PublicKey,
	WebSocketHighLeverageModeConfigAccountSubscriber,
	getHighLeverageModeConfigPublicKey,
} from '@drift-labs/sdk';
import React, { PropsWithChildren, useEffect, useRef, useState } from 'react';
import { usePathname } from 'next/navigation';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import useCurrentSettings from 'src/hooks/useCurrentSettings';
import Env from 'src/environmentVariables/EnvironmentVariables';
import { needsDataRequirement } from 'src/utils/routeDataRequirements';

const INITIAL_CONTEXT_VALUE: {
	highLeverageModeConfigSubscriber:
		| HighLeverageModeConfigAccountSubscriber
		| undefined;
} = {
	highLeverageModeConfigSubscriber: undefined,
};

export const HighLeverageModeConfigSubscriberContext = React.createContext<{
	highLeverageModeConfigSubscriber:
		| HighLeverageModeConfigAccountSubscriber
		| undefined;
}>(INITIAL_CONTEXT_VALUE);

const HighLeverageModeConfigSubscriberProvider = (
	props: PropsWithChildren<any>
) => {
	const pathname = usePathname();
	const connection = useDriftStore((s) => s.connection.current);
	const driftClient = useDriftStore((s) => s.driftClient.client);
	const bulkAccountLoader = useDriftStore((s) => s.currentBulkAccountLoader);
	const [userSettings] = useCurrentSettings();
	const usePolling = userSettings.accountSubscriberType !== 'websocket';

	// Check if current route needs high leverage config subscriptions
	// Enable on trading pages where leverage settings are needed
	const needsLeverageConfig = pathname
		? needsDataRequirement(pathname, 'trading')
		: false;

	const [contextValue, setContextValue] = useState(INITIAL_CONTEXT_VALUE);
	const subscribersRef = useRef(contextValue);

	const createSubscriber = async (
		program: ConstructorParameters<
			typeof PollingHighLeverageModeConfigAccountSubscriber
		>[0],
		usePolling: boolean,
		bulkAccountLoader?: BulkAccountLoader
	) => {
		const publicKey = await getHighLeverageModeConfigPublicKey(
			driftClient?.program.programId as PublicKey
		);
		const sub =
			usePolling && bulkAccountLoader
				? new PollingHighLeverageModeConfigAccountSubscriber(
						program,
						publicKey,
						bulkAccountLoader
					)
				: new WebSocketHighLeverageModeConfigAccountSubscriber(
						program,
						publicKey,
						3000, //todo - confirm this
						Env.defaultCommitmentLevel
					);
		await sub.subscribe();
		return sub;
	};

	// set up subscribers
	useEffect(() => {
		if (
			!needsLeverageConfig ||
			!connection ||
			!driftClient ||
			!driftClient.program ||
			!driftClient.isSubscribed ||
			!bulkAccountLoader
		)
			return;

		const createAndSubscribe = async () => {
			const highLeverageModeConfigSubscriber = await createSubscriber(
				driftClient.program,
				usePolling,
				bulkAccountLoader
			);

			setContextValue({
				highLeverageModeConfigSubscriber,
			});

			subscribersRef.current = {
				highLeverageModeConfigSubscriber,
			};
		};

		const teardown = () => {
			subscribersRef.current?.highLeverageModeConfigSubscriber?.unsubscribe();
			setContextValue(INITIAL_CONTEXT_VALUE);
		};

		createAndSubscribe();

		return teardown;
	}, [
		connection,
		driftClient,
		bulkAccountLoader,
		usePolling,
		needsLeverageConfig,
	]);

	return (
		<HighLeverageModeConfigSubscriberContext.Provider value={contextValue}>
			{props.children}
		</HighLeverageModeConfigSubscriberContext.Provider>
	);
};

export default HighLeverageModeConfigSubscriberProvider;
